import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { publicFormApi } from '../api/publicForms';
import type { FormResponses, FormSubmissionResult } from '../types/publicForm.types';

// Query keys
export const publicFormKeys = {
  all: ['portal', 'publicForms'] as const,
  form: (formSlug: string) => [...publicFormKeys.all, 'form', formSlug] as const,
  page: (formId: string, pageIndex: number) => [...publicFormKeys.all, 'page', formId, pageIndex] as const,
};

// Get form details
export const usePublicForm = (formSlug: string) => {
  return useQuery({
    queryKey: publicFormKeys.form(formSlug),
    queryFn: () => publicFormApi.getForm(formSlug),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!formSlug,
  });
};

// Get form page
export const usePublicFormPage = (formId: string, pageIndex: number) => {
  return useQuery({
    queryKey: publicFormKeys.page(formId, pageIndex),
    queryFn: () => publicFormApi.getFormPage(formId, pageIndex),
    enabled: !!formId && pageIndex >= 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Submit form
export const useSubmitPublicForm = () => {
  const queryClient = useQueryClient();

  return useMutation<FormSubmissionResult, Error, { formId: string; responses: FormResponses }>({
    mutationFn: ({ formId, responses }) => publicFormApi.submitForm(formId, responses),
    onSuccess: (data, variables) => {
      // Invalidate form queries after successful submission
      queryClient.invalidateQueries({ queryKey: publicFormKeys.form(variables.formId) });
    },
  });
};

// Upload file
export const useUploadFormFile = () => {
  return useMutation<any, Error, { formId: string; file: File }>({
    mutationFn: ({ formId, file }) => publicFormApi.uploadFile(formId, file),
  });
};

// Validate field
export const useValidateFormField = () => {
  return useMutation<
    { valid: boolean; error?: string },
    Error,
    { formId: string; questionId: string; value: any }
  >({
    mutationFn: ({ formId, questionId, value }) => 
      publicFormApi.validateField(formId, questionId, value),
  });
};